'use client'

import { useState } from 'react'
import { useFormikContext } from 'formik'
import { RefreshCw } from 'lucide-react'
import { useAuth } from '@/hooks/use-auth'
import { FormInput } from '@/components/form-inputs/form-input'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import type { BillingPlan } from '@/lib/billing/types'
import { parseAmount, type PlanEditorValues } from './model'
import { getFIELD_LABELS, getNUMERIC_FIELDS } from './catalog'
import { formatPlanTimestamp } from './change-format'
import { FieldGrid, Notice } from './fields'
import { useTranslations } from "next-intl"
import { TranslationText } from "@/components/i18n/translation-text"
import { usePlanPresentation } from "./use-plan-presentation"
import type { PlanPresentation } from "./presentation"







interface ProviderSettingsProps {
  planId?: string
  onSyncingChange: (syncing: boolean) => void
  onSynced: (plan: BillingPlan) => void
}

export function ProviderSettings({ planId, onSyncingChange, onSynced }: ProviderSettingsProps) {
  const presentation = usePlanPresentation()

  const NUMERIC_FIELDS = getNUMERIC_FIELDS(useTranslations("PlanCatalog"))
  const FIELD_LABELS = getFIELD_LABELS(useTranslations("PlanCatalog"))




  const tUi = useTranslations("Billing.BillingPlanEditorProviderSettings")

  const { values, dirty, isSubmitting } = useFormikContext<PlanEditorValues>()
  const { fetchWithAuth } = useAuth()
  const [syncing, setSyncing] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const price = parseAmount(values.price_monthly_cents, NUMERIC_FIELDS.price)
  const dashboardUrl = process.env.NEXT_PUBLIC_STRIPE_TEST_MODE === 'true'
    ? 'https://dashboard.stripe.com/test'
    : 'https://dashboard.stripe.com'

  async function sync() {
    if (!planId || dirty || isSubmitting || syncing) return
    setSyncing(true)
    onSyncingChange(true)
    setError(null)
    try {
      const response = await fetchWithAuth(`/api/admin/billing/plans/${planId}/stripe-sync`, { method: 'POST' })
      const data = await response.json()
      if (!response.ok || !data.plan) throw new Error(data.error ?? 'Stripe sync failed')
      onSynced(data.plan)
    } catch (error) {
      setError(error instanceof Error ? error.message : tUi("stripeSyncFailed"))
    } finally {
      setSyncing(false)
      onSyncingChange(false)
    }
  }

  if (values.billing_channel === 'shopify') {
    return (
      <div className="space-y-4">
        {/* One element: switching channels reuses this <p> (browser translation). */}
        <Notice onTint><span><TranslationText>{tUi.rich("tag0Tag1ShopifyPlansDoNotSyncAutomatically", { tag0: chunks => (<strong>{chunks}</strong>), tag1: chunks => (<span>{chunks}</span>) })}</TranslationText></span></Notice>
        <FormInput
          name="shopify_plan_handle"
          value={values.shopify_plan_handle ?? ''}
          label={FIELD_LABELS.shopify_plan_handle}
          placeholder={tUi("growthMonthly")}
          description={tUi("handleOfTheMatchingAppPricingPlanIn")}
        />
      </div>
    )
  }

  return <div className="space-y-4">
    <Notice onTint>{stripeSetupHint(planId, price, values.status, tUi)}</Notice>
    <FieldGrid>
      <FormInput name="stripe_product_id" value={values.stripe_product_id ?? ''} label={tUi("stripeProductID")} readOnly description={tUi("createdAndMaintainedByStripeSync")} />
      <FormInput name="stripe_price_id" value={values.stripe_price_id ?? ''} label={tUi("stripePriceID")} readOnly description={tUi("currentStripePriceLinkedToThisPlan")} />
    </FieldGrid>
    <div className="flex flex-wrap gap-4 text-sm">
      {values.stripe_product_id ? <a className="underline" href={`${dashboardUrl}/products/${encodeURIComponent(values.stripe_product_id)}`} target="_blank" rel="noopener noreferrer"><span key={tUi("openStripeProduct")}>{tUi("openStripeProduct")}</span></a> : null}
      {values.stripe_price_id ? <a className="underline" href={`${dashboardUrl}/prices/${encodeURIComponent(values.stripe_price_id)}`} target="_blank" rel="noopener noreferrer"><span key={tUi("openStripePrice")}>{tUi("openStripePrice")}</span></a> : null}
    </div>
    <div className="flex flex-wrap items-center gap-2 text-sm" role="status">
      <span><span key={tUi("syncStatus")}>{tUi("syncStatus")}</span></span>
      <StripeSyncBadge status={values.stripe_sync_status} />
    </div>
    <p className="text-xs text-muted-foreground">{<span key={tUi("lastSyncValue0", { value0: String(formatSyncTime(values.stripe_synced_at, tUi, presentation)) })}>{tUi("lastSyncValue0", { value0: String(formatSyncTime(values.stripe_synced_at, tUi, presentation)) })}</span>}</p>
    {values.stripe_sync_error ? <p role="alert" className="text-sm text-destructive">{values.stripe_sync_error}</p> : null}
    {error ? <p role="alert" className="text-sm text-destructive">{error}</p> : null}
    {planId && price !== null && price > 0 && values.status !== 'archived' ? <>
      <Button type="button" variant="outline" disabled={dirty || isSubmitting || syncing} onClick={sync}>
        <RefreshCw className={`mr-2 h-4 w-4 ${syncing ? 'animate-spin' : ''}`} />
        <span key={syncing ? tUi("syncing") : syncButtonLabel(values.stripe_sync_status, tUi)}>{syncing ? tUi("syncing") : syncButtonLabel(values.stripe_sync_status, tUi)}</span>
      </Button>
      {dirty ? <p className="text-xs text-muted-foreground"><span key={tUi("saveYourChangesBeforeSyncing")}>{tUi("saveYourChangesBeforeSyncing")}</span></p> : null}
    </> : null}
  </div>
}

function stripeSetupHint(planId: string | undefined, price: number | null, status: string, tUi: ReturnType<typeof useTranslations<"Billing.BillingPlanEditorProviderSettings">>): string {
  if (price === 0) return tUi("freePlansDoNotNeedAStripeProduct")
  if (status === 'archived') return tUi("restoreThisPlanFromThePlanListBefore")
  if (!planId) return tUi("saveThisPaidPlanToCreateItsStripe")
  return tUi("paidStripePlansSyncOnSaveWhenAutomatic")
}

function StripeSyncBadge({ status }: { status: PlanEditorValues['stripe_sync_status'] }) {
  const tUi = useTranslations("Billing.BillingPlanEditorProviderSettings")

  switch (status) {
    case 'synced': return <Badge variant="secondary" className="bg-green-50 text-green-700 dark:bg-green-950 dark:text-green-300"><span key={tUi("synced")}>{tUi("synced")}</span></Badge>
    case 'failed': return <Badge variant="destructive"><span key={tUi("failed")}>{tUi("failed")}</span></Badge>
    case 'pending': return <Badge variant="secondary" className="bg-amber-50 text-amber-700 dark:bg-amber-950 dark:text-amber-300"><span key={tUi("pending")}>{tUi("pending")}</span></Badge>
    case null: return <Badge variant="outline"><span key={tUi("notSynced")}>{tUi("notSynced")}</span></Badge>
  }
}

function formatSyncTime(value: string | null, tUi: ReturnType<typeof useTranslations<"Billing.BillingPlanEditorProviderSettings">>, presentation: PlanPresentation): string {
  if (!value) return tUi("never")
  if (Number.isNaN(Date.parse(value))) return tUi("unknown")
  return formatPlanTimestamp(value, presentation)
}

function syncButtonLabel(status: PlanEditorValues['stripe_sync_status'], tUi: ReturnType<typeof useTranslations<'Billing.BillingPlanEditorProviderSettings'>>): string {
  return tUi(status === 'failed' ? 'retryStripeSync' : 'syncToStripe')
}
